import { NotificationType, isTizen } from "@blacknut/javascript-sdk/dist";
import { GamepadButtonEvent, InputProvider } from "@blacknut/playerwebrtc";
import {
  Button,
  CustomBackSubscription,
  useCustomBack as useBack,
  useCustomBack,
  useNotifications,
  MessageModal,
} from "@blacknut/react-sdk/dist";
import { inputUI, useSpatialNavigation } from "@blacknut/spatialnav-sdk/dist";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePlayerContext } from "../../utils/PlayerContext";
import { PlayerStatus } from "../../utils/PlayerUtils";
import { ModalContext, useModal, ModalSubscription } from "@blacknut/react-sdk/dist";

const useBackConfirmation = ({
  validStatus,
  onConfirmed,
  simple = true,
  inputProvider,
}: {
  validStatus: PlayerStatus[];
  onConfirmed: () => void;
  simple?: boolean;
  inputProvider?: InputProvider;
}) => {
  const { playerStatus } = usePlayerContext();
  const { push: backPush } = useBack();
  const backSubscription = useRef<CustomBackSubscription>();
  const modalSubscription = useRef<ModalSubscription>();
  const { push: pushNotification } = useNotifications();
  const { t } = useTranslation();
  const { resume: resumeSpatialNavigation } = useSpatialNavigation();
  const { push: modalPush } = useContext(ModalContext);
  const oldGameModeRef = useRef(false);
  const onBackCanceled = useCallback(() => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;
    inputUI.gameMode = oldGameModeRef.current;
    inputProvider?.resume(); // mouse & keyboard events
  }, [inputProvider]);

  const onBackConfirmedRef = useRef(false);
  const onBackConfirmed = useCallback(() => {
    onBackConfirmedRef.current = true;
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;
    setTimeout(() => {
      onConfirmed();
    }, 300);
  }, [onConfirmed]);

  // Close modal if still opened
  useEffect(() => {
    return () => {
      modalSubscription.current?.remove();
      modalSubscription.current = undefined;
    };
  }, []);

  const showLeaveGameModal = useCallback(() => {
    modalSubscription.current?.remove();
    modalSubscription.current = modalPush((props) => {
      return (
        <MessageModal
          {...props}
          message={t("dialogs.exitGame.message")}
          title={t("dialogs.exitGame.title")}
          onClose={onBackCanceled}
          onAfterOpen={() => {
            inputProvider?.pause(); // mouse & keyboard events
            oldGameModeRef.current = inputUI.gameMode;
            inputUI.gameMode = false;
            resumeSpatialNavigation();
          }}
          positiveButtonText={t("buttons.ok")}
          negativeButtonText={t("buttons.cancel")}
          onPressPositive={onBackConfirmed}
          onPressNegative={onBackCanceled}
          onRequestClose={onBackCanceled}
        />
      );
    });
  }, [
    modalPush,
    onBackCanceled,
    onConfirmed,
    resumeSpatialNavigation,
    t,
    inputProvider,
  ]);

  const timeAtFirstBack = useRef<number>();
  const longBackTimer = useRef<NodeJS.Timeout>();
  const shownNotificationRef = useRef(false);

  useEffect(() => {
    if (validStatus.indexOf(playerStatus) !== -1) {
      backSubscription.current = backPush((evt) => {
        //Ignore events when modal is opened
        if (modalSubscription.current) {
          return false;
        }

        if (simple || isTizen()) {
          showLeaveGameModal();
        } else {
          if (!evt.repeat) {
            timeAtFirstBack.current = new Date().getTime();
          }
          const time = new Date().getTime(); // prevent single press
          longBackTimer.current && clearTimeout(longBackTimer.current);
          longBackTimer.current = setTimeout(() => {
            if (time - timeAtFirstBack.current! < 500) return;
            showLeaveGameModal();
          }, 500);
          if (!evt.repeat && !shownNotificationRef.current) {
            pushNotification({
              type: NotificationType.INFO,
              time: new Date().getTime(),
              localizedMessage: t("player.long_back"),
            });
            shownNotificationRef.current = true;
          }
        }
        return true;
      });
      return () => {
        backSubscription.current?.remove();
      };
    }
  }, [playerStatus]);

  // handle long back on gamepad when game is running
  // When game is launching, B/BACK event are mapped to keyboard thus it is not required to check against gamepadUIEvent
  const timerGamepad = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (!simple && validStatus.indexOf(playerStatus) !== -1) {
      const sub = inputUI.onGamepadGameEvent().subscribe({
        next: (e) => {
          //Ignore events when modal is opened
          if (modalSubscription.current) {
            return;
          }
          const evt = e as GamepadButtonEvent;
          if (evt.button === "BACK") {
            if (evt.type === "press") {
              timerGamepad.current && clearTimeout(timerGamepad.current);
              timerGamepad.current = setTimeout(() => {
                showLeaveGameModal();
              }, 1500);

              if (!shownNotificationRef.current) {
                pushNotification({
                  type: NotificationType.INFO,
                  time: new Date().getTime(),
                  localizedMessage: t("player.long_back"),
                });
                shownNotificationRef.current = true;
              }
            } else if (e.type === "release") {
              timerGamepad.current && clearTimeout(timerGamepad.current);
              timerGamepad.current = undefined;
            }
          }
        },
      });

      return () => {
        sub.unsubscribe();
      };
    }
  }, [validStatus, playerStatus]);
};
export default useBackConfirmation;
